<template>
    <div class="absolute top-0 w-full h-app-bar-bc">
        <div
            ref="mobileBCRef"
            class="relative h-app-bar-bc flex bg-white dark:bg-blue-800 border-gray-100 dark:border-blue-720 transition-all duration-500"
            :class="breadCrumbsStore.showMobileBC && isMbView ? 'top-app-bar border-b' : '-top-app-bar'"
        >
            <CustomBreadCrumbs />
        </div>
    </div>

    <div
        class="absolute pr-5 max-mob:pr-4 w-full h-app-bar bg-white dark:bg-blue-800 border-b border-gray-100 dark:border-gray-760"
        :class="wrapperClass"
    >
        <div class="flex gap-x-4 w-full justify-between items-center h-full">
            <div
                class="relative h-full flex items-center grow truncate"
                ref="headerRef"
            >
                <div
                    class="h-full flex items-center cursor-pointer"
                    @click="showSideBars"
                >
                    <MenuIcon class="ml-4 text-gray-370 dark:text-white block min-mb:hidden" />
                </div>

                <CustomBreadCrumbs class="hidden min-mb:block" />
            </div>

            <div class="flex items-center gap-x-7 max-mob:gap-x-4">
                <HeaderDownloadStatus
                    :showDownloadPopup="showDownloadPopup"
                    @changeShowDownloadPopup="changeShowDownloadPopup"
                />

                <AccountBalance
                    v-if="isManager || viewBillingPermission"
                    :workspace="workspace"
                />

                <div class="flex items-center gap-x-2">
                    <div class="text-xs text-gray-370 dark:text-white">
                        Live:
                    </div>

                    <div class="text-xs text-green-600 dark:text-green">
                        {{ liveCalls }}
                    </div>
                </div>

                <div class="flex items-center gap-x-2">
                    <div class="text-xs text-gray-370 dark:text-white">
                        Total:
                    </div>

                    <div class="text-xs text-purple">
                        {{ totalCalls }}
                    </div>
                </div>

                <div>
                    <NotificationMenu
                        v-model="notificationMenu"
                        v-if="!isAccountTrialEnded"
                        :smallNotificationMenu="smallNotificationMenu"
                        :unreadNotifications="unreadNotifications"
                        :readNotifications="readNotifications"
                        :loadingRead="notificationsStore.loadingRead"
                        :loadingReadIds="notificationsStore.loadingReadIds"
                        @onRead="notificationsStore.onRead"
                    />

                    <NotificationSmallMenu
                        v-if="!isAccountTrialEnded && unreadNotifications.length"
                        v-model="smallNotificationMenu"
                        :notifications="unreadNotifications"
                        @onOpenNotificationMenu="handleNotificationMenu"
                    />

                    <NotificationPopup
                        v-model="showPopup"
                        :notification="lastUrgentlyNotification"
                        @onRead="notificationsStore.onRead"
                    />
                </div>
            </div>
        </div>

<!--        <TrialVersionInfo-->
<!--            v-if="isAccountTrial"-->
<!--            :showMobileBC="breadCrumbsStore.showMobileBC && isMbView"-->
<!--        />-->
    </div>
</template>

<script setup>
import {
    computed, ref, watch, inject,
} from 'vue'

import AccountBalance from '@/components/accountBalance/AccountBalance.vue'
import CustomBreadCrumbs from '@/components/breadCrumbs/CustomBreadCrumbs.vue'
import NotificationMenu from '@/components/menu/NotificationMenu.vue'
import NotificationSmallMenu from '@/components/menu/NotificationSmallMenu.vue'
import MenuIcon from '@/components/icons/MenuIcon.vue'
import HeaderDownloadStatus from '@/components/download/HeaderDownloadStatus.vue'
import NotificationPopup from '@/components/notifications/NotificationPopup.vue'

import { useBreadCrumbsStore } from '@/store/breadCrumbsStore'
import { useAuthorizationStore } from '@/store/authorizationStore'
import { useNotificationsStore } from '@/store/notificaitonsStore'
import { usePermissionsStore } from '@/store/permissionsStore'
import { useHeaderMetricsStore } from '@/store/headerMetricsStore'

import { viewPorts } from '@/constants/viewPorts'

const props = defineProps({
    mini: {
        type: Boolean,
        default: false,
    },
    showDownloadPopup: {
        type: Boolean,
        default: false,
    },
    marginClass: {
        type: Array,
        default: () => [],
    },
})

const emits = defineEmits(['showSideBars', 'changeShowDownloadPopup'])

const notificationsStore = useNotificationsStore()
const breadCrumbsStore = useBreadCrumbsStore()
const authorizationStore = useAuthorizationStore()
const permissionsStore = usePermissionsStore()
const headerMetricsStore = useHeaderMetricsStore()

const currentViewSize = inject('currentViewSize')

const liveCalls = computed(() => headerMetricsStore.liveCalls)
const totalCalls = computed(() => headerMetricsStore.totalCalls)

const notificationMenu = ref(false)

const wrapperClass = computed(() => [
    ...props.marginClass,
    props.mini && 'min-mb:w-app-bar-min',
    !props.mini && 'min-mb:w-app-bar-max',
])

const smallNotificationMenu = computed({
    get() {
        return notificationsStore.smallNotificationMenu
    },
    set(value) {
        notificationsStore.changeSmallNotificationMenu(value)
    },
})

const isMbView = computed(() => viewPorts.mb >= currentViewSize.value)

const workspace = computed(() => authorizationStore.workspace)
const user = computed(() => authorizationStore.user)
const isAccountTrialEnded = computed(() => authorizationStore.isAccountTrialEnded)
const isManager = computed(() => authorizationStore.isManager)

const viewBillingPermission = computed(() => permissionsStore.permissions.billing.view)

const unreadNotifications = computed(() => notificationsStore.unreadNotifications)
const readNotifications = computed(() => notificationsStore.readNotifications)
const lastUrgentlyNotification = computed(() => notificationsStore.lastUnreadUrgentlyNotification)

const showPopup = ref(false)

function showSideBars() {
    emits('showSideBars')
}

function changeShowDownloadPopup() {
    emits('changeShowDownloadPopup')
}

function handleNotificationMenu(value) {
    notificationMenu.value = value
}

watch(lastUrgentlyNotification, () => {
    showPopup.value = !!lastUrgentlyNotification.value
}, { immediate: true })

watch(user, (user) => {
    if (user && !isAccountTrialEnded.value) {
        notificationsStore.loadNotifications()
    }
}, { immediate: true })
</script>
